import type { MachinePublicHydrated } from "@aura-frontend/omnibus-az-client";
import { useMemo } from "react";
import { Provider } from "react-redux";
import { Outlet, useLoaderData } from "react-router-dom";

import { ModalGroup } from "./components/modals";
import { GameHeaderPortal } from "./components/shared/GameHeader/GameHeaderPortal";
import { MagnusSocketStatus, SocketProvider } from "./components/SocketBroker";
import { useStatTrackingModeInitializer } from "./hooks/useStatTrackingModeInitializer";
import { createTaggingStore } from "./store/store";

type LoaderData = {
  machine: MachinePublicHydrated;
};

const TaggingInitializer = ({
  children,
  machine,
}: {
  children: React.ReactNode;
  machine: MachinePublicHydrated;
}) => {
  useStatTrackingModeInitializer(machine);

  return <>{children}</>;
};

/**
 * TaggingSession is the layout container for all tagging routes.
 *
 * This component wraps all tagging views with:
 * - Redux Provider (tagging-specific store, isolated from magnus-web Zustand)
 * - Socket Context (WebSocket connection for real-time data)
 * - MagnusSocketStatus (displays connection status in the header)
 *
 * Child routes are rendered via <Outlet />
 */
const TaggingSession = () => {
  const { machine } = useLoaderData() as LoaderData;

  // Create store once per component mount
  // Machine info could be used to initialize store state if needed
  const store = useMemo(() => {
    console.log(`TaggingSession mounted for machine: ${machine.name}`);
    return createTaggingStore();
  }, [machine.name]);

  return (
    <Provider store={store}>
      <SocketProvider>
        <MagnusSocketStatus />
        <TaggingInitializer machine={machine}>
          <GameHeaderPortal />
          <ModalGroup />
          <Outlet />
        </TaggingInitializer>
      </SocketProvider>
    </Provider>
  );
};

export default TaggingSession;
